import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Landmark } from "lucide-react";
import api, { eur } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const { clearCart } = useCart();
  const { settings } = useSettings();
  const sessionId = params.get("session_id");
  const codOrderId = params.get("order_id");
  const isCod = params.get("cod") === "1";
  const isBank = params.get("method") === "bank";
  const [state, setState] = useState(isBank ? "bank" : isCod ? "cod" : "checking");
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if ((isCod || isBank) && codOrderId) {
      if (!cleared.current) { clearCart(); cleared.current = true; }
      api.get(`/orders/${codOrderId}`).then(({ data }) => setOrder(data)).catch(() => {});
      return;
    }
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cleared.current) { clearCart(); cleared.current = true; }
          setOrder(data.order);
          setState("paid");
          return;
        }
        if (data.status === "expired") { setState("expired"); return; }
        if (attempts < 8) { attempts++; setTimeout(poll, 2000); }
        else setState("pending");
      } catch {
        if (attempts < 8) { attempts++; setTimeout(poll, 2000); }
        else setState("error");
      }
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const Box = ({ icon, title, text, color }) => (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      <div className={`mx-auto mb-6 ${color}`}>{icon}</div>
      <h1 className="font-serif-display text-3xl text-[#1E3A2F]">{title}</h1>
      <p className="text-[#536D61] mt-3">{text}</p>
      {order && (
        <div className="mt-8 bg-white border border-[#E5E9E4] rounded-sm p-6 text-left">
          <p className="text-sm text-[#536D61]">Αριθμός παραγγελίας</p>
          <p className="font-mono text-[#1E3A2F] mb-4" data-testid="order-id">{order.id}</p>
          <div className="space-y-2">
            {order.items?.map((it, i) => (
              <div key={i} className="flex justify-between text-sm"><span>{it.name} × {it.quantity}</span><span>{eur(it.price * it.quantity)}</span></div>
            ))}
          </div>
          <div className="flex justify-between pt-3 mt-3 border-t border-[#E5E9E4] font-semibold text-[#1E3A2F]"><span>Σύνολο</span><span>{eur(order.total)}</span></div>
        </div>
      )}
      <Link to="/shop" className="mt-8 inline-block bg-[#1E3A2F] text-white px-7 py-3.5 rounded-sm hover:bg-[#111C18]">Συνέχεια αγορών</Link>
    </div>
  );

  if (state === "checking") return <Box icon={<Clock className="h-16 w-16 animate-pulse" />} color="text-[#536D61]" title="Επιβεβαίωση πληρωμής..." text="Παρακαλώ περιμένετε." />;
  if (state === "paid") return <Box icon={<CheckCircle2 className="h-16 w-16" />} color="text-[#4A5D23]" title="Η παραγγελία ολοκληρώθηκε!" text="Σας ευχαριστούμε. Θα λάβετε ενημέρωση για την παράδοση." />;
  if (state === "cod") return <Box icon={<CheckCircle2 className="h-16 w-16" />} color="text-[#4A5D23]" title="Η παραγγελία καταχωρήθηκε!" text="Θα πληρώσετε με αντικαταβολή κατά την παράδοση. Σας ευχαριστούμε!" />;
  if (state === "bank") {
    const bank = settings?.payment?.bank_details || {};
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <div className="mx-auto mb-6 text-[#4A5D23]"><CheckCircle2 className="h-16 w-16 mx-auto" /></div>
        <h1 className="font-serif-display text-3xl text-[#1E3A2F]">Η παραγγελία καταχωρήθηκε!</h1>
        <p className="text-[#536D61] mt-3">Ολοκληρώστε την πληρωμή με κατάθεση στον παρακάτω λογαριασμό, χρησιμοποιώντας ως αιτιολογία τον αριθμό παραγγελίας.</p>
        <div className="mt-6 bg-[#E8F0E1] border border-[#CBD9B6] rounded-sm p-6 text-left">
          <div className="flex items-center gap-2 text-[#1E3A2F] font-semibold mb-3"><Landmark className="h-5 w-5" /> Στοιχεία κατάθεσης</div>
          {bank.bank_name && <p className="text-sm text-[#2a3a33]">Τράπεζα: {bank.bank_name}</p>}
          {bank.account_name && <p className="text-sm text-[#2a3a33]">Δικαιούχος: {bank.account_name}</p>}
          {bank.iban ? <p className="text-sm text-[#2a3a33]">IBAN: <span className="font-mono">{bank.iban}</span></p> : <p className="text-sm text-[#536D61]">Τα στοιχεία θα σταλούν με email.</p>}
          {bank.notes && <p className="text-sm text-[#536D61] mt-2">{bank.notes}</p>}
        </div>
        {order && (
          <div className="mt-6 bg-white border border-[#E5E9E4] rounded-sm p-6 text-left">
            <p className="text-sm text-[#536D61]">Αριθμός παραγγελίας (αιτιολογία)</p>
            <p className="font-mono text-[#1E3A2F] mb-4" data-testid="order-id">{order.id}</p>
            <div className="flex justify-between font-semibold text-[#1E3A2F]"><span>Ποσό προς κατάθεση</span><span>{eur(order.total)}</span></div>
          </div>
        )}
        <Link to="/shop" className="mt-8 inline-block bg-[#1E3A2F] text-white px-7 py-3.5 rounded-sm hover:bg-[#111C18]">Συνέχεια αγορών</Link>
      </div>
    );
  }
  if (state === "pending") return <Box icon={<Clock className="h-16 w-16" />} color="text-[#8a5a1e]" title="Η πληρωμή εκκρεμεί" text="Θα ενημερωθείτε μόλις επιβεβαιωθεί η πληρωμή." />;
  if (state === "expired") return <Box icon={<XCircle className="h-16 w-16" />} color="text-[#C25953]" title="Η συνεδρία έληξε" text="Δοκιμάστε ξανά την πληρωμή." />;
  return <Box icon={<XCircle className="h-16 w-16" />} color="text-[#C25953]" title="Κάτι πήγε στραβά" text="Επικοινωνήστε μαζί μας για βοήθεια." />;
}
